import React, { useState } from 'react';
import { Mail, Users, TrendingUp, Award, ArrowRight, Briefcase, Target, Zap, ShieldCheck, UsersRound, Building, Lightbulb, BarChartBig, X } from 'lucide-react';
import clientAxios from '../api/clientAxios';
import jointeam from '../assets/videos/UneteANuestroEquipo.mp4';
import INCF from '../assets/images/JoinCampoFresco.png';
import loadingGIF from '../assets/images/productos/GIF/GIF.gif'


const CareersSection = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalArea, setModalArea] = useState("");
  const [form, setForm] = useState({ nombre: '', apellido: '', correo: '', texto: '', archivo: null });
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const benefits = [
    { 
      icon: ShieldCheck,
      title: "Liderazgo en el Mercado",
      description: "Forma parte de una empresa consolidada y reconocida en la industria alimentaria."
    },
    { 
      icon: TrendingUp,
      title: "Desarrollo Profesional", 
      description: "Ofrecemos programas de capacitación y oportunidades de crecimiento continuo."
    },
    { 
      icon: UsersRound,
      title: "Ambiente Colaborativo",
      description: "Trabaja en un entorno dinámico y de apoyo mutuo con equipos multidisciplinarios."
    },
    { 
      icon: Lightbulb,
      title: "Impacto y Sostenibilidad",
      description: "Contribuye a proyectos innovadores que promueven la sostenibilidad en el sector."
    }
  ];

  const areas = [
    { name: 'Cadena de Suministro', icon: BarChartBig, description: "Optimiza la logística y distribución de nuestros productos a nivel nacional." },
    { name: 'Ventas y Desarrollo de Negocios', icon: Target, description: "Expande nuestra presencia en el mercado y construye relaciones sólidas con clientes." },
    { name: 'Finanzas y Contabilidad', icon: Award, description: "Gestiona los recursos financieros y asegura la estabilidad económica de la empresa." },
    { name: 'Tecnología e Innovación', icon: Zap, description: "Implementa soluciones tecnológicas para mejorar nuestros procesos y productos." },
    { name: 'Recursos Humanos', icon: Users, description: "Atrae, desarrolla y gestiona el talento que impulsa nuestro éxito." },
    { name: 'Producción y Calidad', icon: Briefcase, description: "Asegura la excelencia operativa y la calidad de nuestros alimentos." }
  ];

  const stats = [
    { number: "500+", label: "Colaboradores", sublabel: "forman nuestro equipo" },
    { number: "40+", label: "Años de Trayectoria", sublabel: "innovando en la industria" }, // Actualizado para mayor impacto
    { number: "Nacional", label: "Presencia", sublabel: "en las principales ciudades" }
  ];

  const handleOpenModal = (area) => {
    setModalArea(area);
    setModalOpen(true);
    setForm({ nombre: '', apellido: '', correo: '', texto: '', archivo: null });
    setFormError("");
    setFormSuccess("");
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleFormChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'archivo') {
      setForm({ ...form, archivo: files[0] });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");
    setIsLoading(true);
    if (!form.nombre || !form.apellido || !form.correo || !form.texto || !form.archivo) {
      setFormError("Por favor completa todos los campos y adjunta tu CV en PDF.");
      setIsLoading(false);
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.correo)) {
      setFormError("Correo electrónico inválido.");
      setIsLoading(false);
      return;
    }
    if (form.archivo && form.archivo.type !== 'application/pdf') {
      setFormError("El archivo debe ser un PDF.");
      setIsLoading(false);
      return;
    }
    try {
      const formData = new FormData();
      formData.append('nombre', form.nombre);
      formData.append('apellido', form.apellido);
      formData.append('correo', form.correo);
      formData.append('texto', form.texto);
      formData.append('area', modalArea);
      formData.append('archivo', form.archivo);
      await clientAxios.post('/postulacion', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormSuccess("¡Tu postulación ha sido enviada correctamente!");
      setTimeout(() => setModalOpen(false), 2000);
    } catch (err) {
      setFormError(err.response?.data?.error || err.message || 'Error al enviar la postulación.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-gray-800">
      {/* Modal de postulación */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl p-0 w-full max-w-5xl relative animate-fade-in flex flex-col md:flex-row overflow-hidden">
            {/* Imagen lado izquierdo */}
            <div className="hidden md:flex md:w-1/2 items-center justify-center bg-slate-100 order-1">
              <img 
                src={INCF} 
                alt="Campo Fresco ICF Logo" 
                className="w-[100rem] h-[28rem] object-contain drop-shadow-2xl filter brightness-90 m-6"
                style={{ width: '100%', height: '100%', 
                  objectFit: 'cover',
                }}
              />
            </div>
            {/* Formulario lado derecho */}
            <div className="w-full md:w-1/2 p-10 relative order-2">
              <button onClick={handleCloseModal} className="absolute top-4 right-4 text-gray-400 hover:text-red-500">
                <X size={28} />
              </button>
              <h2 className="text-2xl font-bold mb-2 text-black">Postulación para {modalArea}</h2>
              <form className="space-y-4 mt-4" onSubmit={handleFormSubmit} encType="multipart/form-data">
                <div className="flex gap-3">
                  <input type="text" name="nombre" placeholder="Nombre" value={form.nombre} onChange={handleFormChange} className="w-1/2 px-4 py-2 border rounded-lg focus:ring-emerald-500" />
                  <input type="text" name="apellido" placeholder="Apellido" value={form.apellido} onChange={handleFormChange} className="w-1/2 px-4 py-2 border rounded-lg focus:ring-emerald-500" />
                </div>
                <input type="email" name="correo" placeholder="Correo electrónico" value={form.correo} onChange={handleFormChange} className="w-full px-4 py-2 border rounded-lg focus:ring-emerald-500" />
                <textarea name="texto" placeholder="Cuéntanos por qué quieres unirte a esta área..." value={form.texto} onChange={handleFormChange} className="w-full px-4 py-2 border rounded-lg focus:ring-emerald-500 min-h-[80px]" />
                <div>
                  <label className="block text-sm font-medium mb-1">Adjuntar CV (.pdf)</label>
                  <input type="file" name="archivo" accept="application/pdf" onChange={handleFormChange} className="w-full" />
                </div>
                {isLoading && (
                  <img src={loadingGIF} alt="Cargando..." className="w-25 h-25 mx-auto mt-4" style={{ width: '100px', height: '100px' }} />
                )}
                {formError && <div className="text-red-600 text-sm">{formError}</div>}
                {formSuccess && <div className="text-green-600 text-sm">{formSuccess}</div>}
                <button type="submit" className="w-full bg-emerald-600 text-white font-semibold py-3 rounded-lg hover:bg-emerald-700 transition-colors" disabled={isLoading}>Enviar Postulación</button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section con video de fondo */}
      <section className="relative h-[40vh] sm:h-[50vh] md:h-[70vh] flex items-center justify-center overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover z-0 max-h-[40vh] sm:max-h-[50vh] md:max-h-[70vh] min-h-[200px]"
          src={jointeam}
          autoPlay
          loop
          muted
          playsInline
          style={{ objectFit: 'cover', width: '100%', height: '100%' }}
        />
        <div className="absolute inset-0 bg-emerald-900/60 z-10" />
        <div className="relative z-20 w-full text-center flex flex-col items-center justify-end px-4 md:px-6 pb-8 md:pb-12 h-full">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight text-white drop-shadow-lg">
            Desarrolla tu Potencial en Campo Fresco
          </h1>
          <p className="text-base md:text-xl text-green-100 mb-6 md:mb-10 max-w-2xl mx-auto">
            Buscamos talento apasionado por la industria alimentaria, comprometido con la calidad y la innovación. ¡Tu oportunidad de crecer es aquí!
          </p>
          <a 
            href="#oportunidades"
            className="bg-white text-emerald-700 font-semibold py-2 md:py-3 px-6 md:px-8 rounded-lg shadow-md hover:bg-green-50 transition-colors duration-300 text-base md:text-lg"
          >
            Ver Oportunidades
          </a>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-6">¿Por Qué Elegir Campo Fresco?</h2>
          <p className="text-center text-gray-600 mb-12 md:mb-16 max-w-2xl mx-auto">
            Te ofrecemos un entorno donde podrás crecer profesionalmente, innovar y contribuir a una industria esencial.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="bg-slate-50 p-6 rounded-xl shadow-lg hover:shadow-emerald-100 transition-shadow duration-300 flex flex-col items-center text-center">
                  <div className="bg-emerald-500 text-white p-4 rounded-full mb-4 inline-block">
                    <Icon size={32} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Areas of Opportunity Section */}
      <section id="oportunidades" className="py-16 md:py-24 bg-slate-100">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-16">Explora Nuestras Áreas de Oportunidad</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {areas.map((area, index) => {
              const Icon = area.icon;
              return (
                <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300 flex flex-col">
                  <div className="flex items-center mb-4">
                    <div className="bg-emerald-100 text-emerald-600 p-3 rounded-lg mr-4">
                      <Icon size={28} />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800">{area.name}</h3>
                  </div>
                  <p className="text-gray-600 mb-6 text-sm flex-grow">{area.description}</p>
                  <button
                    type="button"
                    onClick={() => handleOpenModal(area.name)}
                    className="mt-auto inline-flex items-center justify-center text-emerald-600 font-semibold hover:text-emerald-700 group"
                  >
                    Postularme a esta área
                    <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats/Impact Section */}
      <section className="py-16 md:py-24 bg-emerald-600 text-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Nuestro Impacto y Equipo</h2>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index} className="p-6 bg-emerald-700/50 rounded-xl">
                <div className="text-4xl lg:text-5xl font-bold mb-2">{stat.number}</div>
                <div className="text-lg font-semibold text-emerald-100">{stat.label}</div>
                <p className="text-sm text-emerald-200">{stat.sublabel}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <Building size={48} className="mx-auto text-emerald-500 mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">¿Listo para Unirte a Nuestro Equipo?</h2>
          <p className="text-gray-600 mb-8 text-lg leading-relaxed">
            Si compartes nuestra pasión por la calidad y la innovación en la industria alimentaria, queremos conocerte. Envíanos tu CV y cuéntanos cómo puedes contribuir a Campo Fresco.
          </p>
          <div className="bg-slate-100 p-8 rounded-xl shadow-md inline-block">
            <p className="text-gray-700 mb-1 text-sm">Contacta a nuestro equipo de Recursos Humanos:</p>
            <a 
              href="mailto:reclutamiento@campofresco.com.mx" 
              className="text-xl font-semibold text-emerald-600 hover:text-emerald-700 transition-colors duration-300 inline-flex items-center gap-2"
            >
              <Mail size={22} /> reclutamiento@campofresco.com.mx
            </a>
            {/* <p className="mt-2 text-gray-700 text-sm">
              o llama al:
            </p>
            <a 
              href="tel:+5214626218268" 
              className="text-xl font-semibold text-emerald-600 hover:text-emerald-700 transition-colors duration-300 inline-flex items-center gap-2 mt-1"
            >
              <Briefcase size={22} /> +52 (462) 621 8268
            </a> */}
            <p className="text-xs text-gray-500 mt-4">¡Esperamos tu postulación!</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CareersSection;